import { InfoModal, visuallyHiddenStyles } from "@newsela/angelou";
import PropTypes from "prop-types";
import { useFormState, useFormContext } from "react-hook-form";

import { AdminContentSettingsSearchFormBanner } from "@/admin/AdminContentSettingsSearchFormOverlay/AdminContentSettingsSearchFormBanner/AdminContentSettingsSearchFormBanner";
import { AdminContentSettingsSearchFormOverlayButtons } from "@/admin/AdminContentSettingsSearchFormOverlay/AdminContentSettingsSearchFormOverlayButtons/AdminContentSettingsSearchFormOverlayButtons";
import { useAdminContentSettingsContext } from "@/search/AdminContentSettingsContext/AdminContentSettingsContext";
import { confirmationModalCss } from "./AdminContentSettingsSearchFormOverlay.style";

const propTypes = {
  invokeContentSettingsSearchModeChange: PropTypes.func.isRequired,
};

const AdminContentSettingsSearchFormOverlay = ({
  invokeContentSettingsSearchModeChange,
}) => {
  // initialize everything needed from the context provider
  const { applyPermissionsModalProps } = useAdminContentSettingsContext();

  const { closeModal } = applyPermissionsModalProps;

  // initialize react-hook-form which drives the overlay components
  // the overlay components are responsible for submitting the form
  // with flagged content item selections
  // overlay components: Apply Permissions banner, Apply Permissions modal
  const { reset, watch, trigger, control, getValues, handleSubmit } =
    useFormContext();
  const { isDirty, defaultValues } = useFormState({ control });

  // find article changes by comparing defaultValues and
  // currentValues from react-hook-form state every time
  // the form state changes
  const getArticleChanges = (defaultValues, currentValues) => {
    // commpare default group is_flagged state with current group to see if it changed
    const isGroupDirty = ({ contentId, group, index }) => {
      return group.is_flagged !== defaultValues[contentId][index].is_flagged;
    };

    // reduce articles so we can create an object with only those articles that changed.
    const articlesReducer = (articles, contentId) => {
      const dirtyGroups = currentValues[contentId].filter((group, index) =>
        isGroupDirty({ contentId, group, index }),
      );
      if (dirtyGroups.length > 0) {
        articles[contentId] = dirtyGroups;
      }
      return articles;
    };

    // apply the reducer to get the article changes
    const articles = Object.keys(defaultValues).reduce(articlesReducer, {});

    return {
      articleChanges: articles,
      articleChangesCount: Object.keys(articles).length,
    };
  };

  // Get article changes
  const { articleChanges } = getArticleChanges(defaultValues, getValues());

  // listen for new changes
  watch();

  return (
    <>
      <InfoModal
        labelId="modal-label"
        title="Finish applying permissions"
        ariaProps={{ "aria-label": "Finish applying permissions" }}
        __cssFor={confirmationModalCss}
        {...applyPermissionsModalProps}
        onBackgroundClick={closeModal}
        footer={
          <AdminContentSettingsSearchFormOverlayButtons
            reset={reset}
            trigger={trigger}
            closeModal={closeModal}
            handleSubmit={handleSubmit}
            articleChanges={articleChanges}
            invokeContentSettingsSearchModeChange={
              invokeContentSettingsSearchModeChange
            }
          />
        }
      >
        Before moving on, select "Apply permissions" to save your changes.
      </InfoModal>

      <div role="region" aria-label="Selected content">
        <h2 css={visuallyHiddenStyles}>Selected content</h2>
        <p css={visuallyHiddenStyles}>
          Content that you select to be flagged will be listed here. After
          making a selection, you'll have the option to cancel or apply
          visibility permissions
        </p>
      </div>

      {/* isDirty means there are unsaved selection/deselections */}
      {isDirty && (
        <AdminContentSettingsSearchFormBanner
          reset={reset}
          trigger={trigger}
          handleSubmit={handleSubmit}
          articleChanges={articleChanges}
        />
      )}
    </>
  );
};

AdminContentSettingsSearchFormOverlay.propTypes = propTypes;

export default AdminContentSettingsSearchFormOverlay;
